/**
 * Newsletter form handler.
 *
 * Provider-agnostic. Reads its config from the `newsletter` data module
 * and:
 *   - 'mailto'       -> opens the user's mail client with a pre-filled
 *                       subject/body (default until a real provider is set up)
 *   - 'buttondown'   -> submits to https://buttondown.email/<username>
 *   - 'mailchimp'    -> posts to the configured action URL
 *   - 'convertkit'   -> posts to https://app.convertkit.com/forms/<id>/subscriptions
 *   - 'emailoctopus' -> the form markup is injected by EmailOctopus's
 *                       embed script, which sets a real `action` on the
 *                       <form> element; we let the browser submit it
 *                       natively and just report "Submitting…" status
 *
 * In every case we also render a fallback link in case the user is on
 * a browser that doesn't allow programmatic form submission, and we
 * surface a success state without reloading the page.
 */

import { newsletter } from './data/content.js';

function appendFallback(form, message) {
  const fallback = document.querySelector('.newsletter_fallback');
  if (!fallback) return;
  if (message) fallback.textContent = message;
  fallback.hidden = false;
  form.hidden = true;
}

function setStatus(form, message, state) {
  const status = form.querySelector('.newsletter_status');
  if (!status) return;
  status.textContent = message;
  status.dataset.state = state || '';
}

export function setupNewsletter() {
  const form = document.querySelector('[data-newsletter-form]');
  if (!form) return;

  // If the form already has a real action attribute (and isn't the
  // mailto: pseudo-action), let the browser submit it natively.
  const action = form.getAttribute('action') || '';
  if (action && !action.startsWith('mailto:') && newsletter.provider !== 'mailto') {
    form.addEventListener('submit', () => {
      setStatus(form, 'Submitting\u2026', 'pending');
    });
    return;
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const input = form.querySelector('input[type="email"]');
    const email = input ? input.value.trim() : '';
    if (!email) return;

    if (newsletter.provider === 'mailto') {
      const subject = encodeURIComponent('Newsletter signup');
      const body = encodeURIComponent(`Please add ${email} to the Senna Grey newsletter.`);
      window.location.href = `mailto:${'hello@sennagrey.com'}?subject=${subject}&body=${body}`;
      appendFallback(
        form,
        `If your mail app didn't open, email hello@sennagrey.com and we'll add you when the newsletter is ready.`,
      );
      return;
    }

    // For real providers, fall through to a programmatic submit.
    form.submit();
  });
}
